import { FC, forwardRef } from 'react';
import {
  TBundle,
  TSession,
} from '@fe-learner-common/api/schemas/sessionsSchema';
import * as Dialog from '@radix-ui/react-dialog';
import { publishAnalyticsEvent } from '@utils/publishAnalyticsEvent';
import { cn, CourseImage, Modal, Tag } from '@fe-learner-common';
import { useIntl } from 'react-intl';
import { TW_ROOT } from '@utils/constants';
import { toLocaleDateTimeLong } from '@fe-learner-common/utils/date';
import { useSessionTranslations } from '@hooks/useSessionTranslations';
import { useSessionTags } from '@hooks/useSessionTags';
import { useBundleThumbnail, useSessionThumbnail } from '@hooks/useImageUrl';
import { useSelectedBundleId } from '@routes/catalog/hooks/useSelectedBundleId';
import { Link, useSearchParams } from 'react-router-dom';

type CourseCardProps = {
  bundle: TBundle;
  sessions?: TSession[] | null;
};

const Card: FC<CourseCardProps> = forwardRef<HTMLDivElement, CourseCardProps>(
  ({ bundle }, ref) => {
    const intl = useIntl();
    const thumbnail = useBundleThumbnail({ bundle });

    return (
      <div ref={ref} className="lf:relative lf:flex lf:w-full lf:flex-col lf:gap-y-3">
        <CourseImage
          thumbnail={thumbnail}
          alt={intl.formatMessage({ id: 'courses.project.card.image.alt' })}
        />
        <div className="lf:flex lf:flex-col lf:items-start">
          <div className="lf:flex lf:items-center">
            <h3 className="lf:py-1.5 lf:text-label-lg">{bundle.name}</h3>
          </div>
          <p className="lf:flex lf:gap-2 lf:text-body-sm lf:text-neutral-500">
            {intl.formatMessage({ id: 'catalog.collection' })}
            <Separator />
            {intl.formatMessage(
              {
                id: 'learner.common.nrOfCourses',
              },

              { nrOfCourses: bundle.sessions.length },
            )}
          </p>
        </div>
      </div>
    );
  },
);
Card.displayName = 'Card';

const Separator = () => <span>·</span>;
const CollectionCardListItem: FC<{ session: TSession }> = ({ session }) => {
  const texts = useSessionTranslations(session);
  const tags = useSessionTags(session);
  const thumbnail = useSessionThumbnail({ session, size: 144 });
  return (
    <li
      className={cn(
        'lf:relative ',
        'lf:after:absolute lf:after:-bottom-1 lf:after:left-4 lf:after:right-4 lf:after:z-[-1] lf:after:box-content lf:after:border-b-1 lf:after:border-neutral-200 lf:after:content-[""]',
        '[&:last-of-type]:after:border-none',
      )}
    >
      <Link
        key={`bundle-session-${session.uuid}`}
        className={cn(
          'lf:l-btn-tertiary lf:flex lf:w-full lf:items-start lf:gap-5 lf:rounded-lg lf:p-4 lf:text-neutral-900',
        )}
        to={`./session/${session.uuid}`}
        onClick={() => {
          publishAnalyticsEvent(
            {
              context: 'CatalogPage',
              action: 'CourseCardClicked',
            },
            {
              courseId: session?.uuid,
            },
          );
        }}
      >
        <CourseImage
          className="lf:rounded-lg"
          width={72}
          alt={session.name || ''}
          thumbnail={thumbnail}
        />
        <div>
          <h3 className="lf:pb-1 lf:text-label-lg">{session.name}</h3>
          <div className="lf:flex lf:flex-wrap lf:gap-2 lf:text-body-md lf:text-neutral-500">
            {session.startDate && (
              <>
                <span>{toLocaleDateTimeLong(session.startDate)}</span>
                <Separator />
              </>
            )}

            {session.location && <span>{session.location}</span>}
            {!session.location && session.format && <span>{texts.format}</span>}

            {tags
              ? Object.entries(tags).map(([key, value]) => (
                  <Tag
                    key={`tag-bundle-${session.uuid}-${key}`}
                    icon={value.icon}
                    type={value.type}
                  >
                    {value.text}
                  </Tag>
                ))
              : null}
          </div>
        </div>
      </Link>
    </li>
  );
};

export const CollectionCard: FC<CourseCardProps> = ({ bundle, sessions }) => {
  const intl = useIntl();
  const [selectedBundleId, setSelectedBundleId] = useSelectedBundleId();
  const [searchParams] = useSearchParams();

  // Keep the current category/filter params so the list behind the dialog doesn't reset.
  const bundleSearchParams = new URLSearchParams(searchParams);
  bundleSearchParams.set('bundle', String(bundle.id));

  const handleOpenChange = (open: boolean) => {
    setSelectedBundleId(open ? bundle.id : null);
  };

  const Trigger = () => (
    <Dialog.Trigger asChild>
      <Link
        className="lf:flex lf:h-full lf:cursor-pointer lf:flex-col lf:gap-3 lf:rounded-2xl lf:p-3 lf:hover:bg-neutral-100 lf:sm:m-0 lf:sm:size-auto"
        to={{
          pathname: '/',
          search: `?${bundleSearchParams.toString()}`,
        }}
      >
        <Card bundle={bundle} />
      </Link>
    </Dialog.Trigger>
  );

  const title = (
    <div>
      <p className="lf:text-body-sm lf:text-neutral-500">
        {intl.formatMessage({ id: 'catalog.collection' })}
      </p>

      <h1 className="lf:text-title-md">{bundle.name}</h1>
    </div>
  );

  return (
    <Modal
      container={document.getElementById(TW_ROOT) as HTMLElement}
      className="lf:z-[1010] lf:md:w-[640px]"
      Trigger={Trigger}
      TitleContent={title}
      open={selectedBundleId === bundle.id}
      onOpenChange={handleOpenChange}
    >
      <ul className="lf:flex lf:flex-col lf:gap-y-2 lf:p-2">
        {sessions?.map((session) => (
          <CollectionCardListItem
            key={`bundle-session-${session.uuid}`}
            session={session}
          />
        ))}
      </ul>
    </Modal>
  );
};
